class CubicoHulkenVariantGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.galleryMap = JSON.parse(this.querySelector('[data-gallery-map]')?.textContent || '{}');
    this.deliveryStatusMap = JSON.parse(this.querySelector('[data-delivery-status-map]')?.textContent || '{}');
    this.fallbackImages = JSON.parse(this.querySelector('[data-gallery-fallback]')?.textContent || '[]');
    this.thumbs = this.querySelector('[data-gallery-thumbs]');
    this.dots = this.querySelector('[data-gallery-dots]');
    this.mainImage = this.querySelector('[data-gallery-main-image]');
    this.status = this.querySelector('[data-gallery-status]');
    this.activeImages = [];
    this.activeIndex = 0;
    this.touchStartX = null;

    this.onClick = (event) => {
      const control = event.target.closest('[data-gallery-thumb], [data-gallery-dot]');
      if (!control || !this.contains(control)) return;
      const index = Number(control.dataset.galleryThumb ?? control.dataset.galleryDot);
      this.showImage(index);
    };
    this.addEventListener('click', this.onClick);

    this.stage = this.querySelector('[data-gallery-stage]');
    this.onTouchStart = (event) => {
      this.touchStartX = event.changedTouches[0]?.clientX ?? null;
    };
    this.onTouchEnd = (event) => {
      if (this.touchStartX === null) return;
      const delta = (event.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(delta) < 40) return;
      this.showImage(this.activeIndex + (delta < 0 ? 1 : -1));
    };
    this.stage?.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.stage?.addEventListener('touchend', this.onTouchEnd, { passive: true });

    this.productInfo = this.closest('product-info');
    this.onVariantInputChange = (event) => {
      if (!event.target.closest('variant-selects')) return;
      this.syncFromNativeVariantInput();
    };
    this.productInfo?.addEventListener('change', this.onVariantInputChange);

    this.renderVariant(String(this.dataset.variantId));
  }

  disconnectedCallback() {
    clearInterval(this.variantSyncTimer);
    this.productInfo?.removeEventListener('change', this.onVariantInputChange);
    this.removeEventListener('click', this.onClick);
    this.stage?.removeEventListener('touchstart', this.onTouchStart);
    this.stage?.removeEventListener('touchend', this.onTouchEnd);
    this.initialized = false;
  }

  syncFromNativeVariantInput() {
    let attempts = 0;
    clearInterval(this.variantSyncTimer);
    this.variantSyncTimer = setInterval(() => {
      attempts += 1;
      const variantId = this.productInfo?.querySelector('product-form form[action*="/cart/add"] input[name="id"]')?.value;
      if (variantId && this.galleryMap[String(variantId)] && String(variantId) !== String(this.dataset.variantId)) {
        clearInterval(this.variantSyncTimer);
        this.renderVariant(String(variantId));
      } else if (attempts >= 80) {
        clearInterval(this.variantSyncTimer);
      }
    }, 100);
  }

  renderVariant(variantId) {
    const configured = this.galleryMap[variantId] || [];
    this.activeImages = configured.length ? configured : this.fallbackImages;
    this.dataset.variantId = variantId;
    this.dataset.gallerySource = configured.length ? 'variant-metafield' : 'product-fallback';
    this.updateDeliveryStatus(variantId);
    this.activeIndex = 0;
    this.thumbs.innerHTML = '';
    this.dots.innerHTML = '';

    this.activeImages.forEach((image, index) => {
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = `cubico-hulken-gallery__thumb${index === 0 ? ' is-active' : ''}`;
      thumb.dataset.galleryThumb = String(index);
      thumb.setAttribute('aria-label', `View image ${index + 1}`);
      const img = document.createElement('img');
      img.src = image.thumb || image.src;
      img.alt = image.alt || '';
      img.loading = index === 0 ? 'eager' : 'lazy';
      thumb.appendChild(img);
      this.thumbs.appendChild(thumb);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `cubico-hulken-gallery__dot${index === 0 ? ' is-active' : ''}`;
      dot.dataset.galleryDot = String(index);
      dot.setAttribute('aria-label', `View image ${index + 1}`);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      this.dots.appendChild(dot);
    });

    this.showImage(0);
    if (this.status) this.status.textContent = `${this.activeImages.length} images for selected variant`;
  }

  updateDeliveryStatus(variantId) {
    const status = this.deliveryStatusMap[variantId];
    if (!status) return;
    this.productInfo?.querySelectorAll('[data-cubico-delivery-status]').forEach((node) => {
      node.textContent = status;
      node.closest('label')?.setAttribute('data-delivery-status', status.toLowerCase().replaceAll(' ', '-'));
    });
  }

  showImage(index) {
    if (!this.activeImages.length || !this.mainImage) return;
    this.activeIndex = Math.max(0, Math.min(index, this.activeImages.length - 1));
    const image = this.activeImages[this.activeIndex];
    this.querySelectorAll('[data-gallery-thumb]').forEach((node, i) => node.classList.toggle('is-active', i === this.activeIndex));
    this.querySelectorAll('[data-gallery-dot]').forEach((node, i) => {
      node.classList.toggle('is-active', i === this.activeIndex);
      node.setAttribute('aria-current', i === this.activeIndex ? 'true' : 'false');
    });
    if (this.mainImage.src !== new URL(image.src, location.href).href) {
      this.mainImage.classList.add('is-fading');
      const clearFade = () => this.mainImage.classList.remove('is-fading');
      this.mainImage.addEventListener('load', clearFade, { once: true });
      this.mainImage.srcset = '';
      this.mainImage.src = image.src;
      setTimeout(clearFade, 450);
    }
    this.mainImage.alt = image.alt || '';
  }
}

if (!customElements.get('cubico-hulken-variant-gallery')) {
  customElements.define('cubico-hulken-variant-gallery', CubicoHulkenVariantGallery);
}
